/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AcmsAircraftServiceItemResponse } from '../models/acms-aircraft-service-item-response';
import { CreateAcmsAircraftServiceItemRequest } from '../models/create-acms-aircraft-service-item-request';
import { GenericResponse } from '../models/generic-response';
import { GetAcmsAircraftServiceItemResponse } from '../models/get-acms-aircraft-service-item-response';
import { UpdateAcmsAircraftServiceItemRequest } from '../models/update-acms-aircraft-service-item-request';

/**
 * ACMS Aircraft Service item APIs
 */
@Injectable({ providedIn: 'root' })
export class AcmsAircraftServiceItemService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllAircraftServiceItem()` */
  static readonly GetAllAircraftServiceItemPath =
    '/api/v1/acms-aircraft-service-item';

  /**
   * Get list of aircraft service items.
   *
   * Gets the list of aircraft service items.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllAircraftServiceItem()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAircraftServiceItem$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This enables filtering of aircraft service items based on the aircraft id
       */
      aircraftId?: number;

      /**
       * This enables filtering of aircraft service items based on the service item id
       */
      serviceItemId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetAcmsAircraftServiceItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftServiceItemService.GetAllAircraftServiceItemPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('aircraftId', params.aircraftId, {});
      rb.query('serviceItemId', params.serviceItemId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetAcmsAircraftServiceItemResponse>;
        })
      );
  }

  /**
   * Get list of aircraft service items.
   *
   * Gets the list of aircraft service items.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllAircraftServiceItem$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAircraftServiceItem(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This enables filtering of aircraft service items based on the aircraft id
       */
      aircraftId?: number;

      /**
       * This enables filtering of aircraft service items based on the service item id
       */
      serviceItemId?: number;
    },
    context?: HttpContext
  ): Observable<GetAcmsAircraftServiceItemResponse> {
    return this.getAllAircraftServiceItem$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetAcmsAircraftServiceItemResponse>
        ): GetAcmsAircraftServiceItemResponse => r.body
      )
    );
  }

  /** Path part for operation `createAcmsAircraftServiceItem()` */
  static readonly CreateAcmsAircraftServiceItemPath =
    '/api/v1/acms-aircraft-service-item';

  /**
   * Create service item against acms aircraft.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAcmsAircraftServiceItem()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAcmsAircraftServiceItem$Response(
    params: {
      body: CreateAcmsAircraftServiceItemRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AcmsAircraftServiceItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftServiceItemService.CreateAcmsAircraftServiceItemPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AcmsAircraftServiceItemResponse>;
        })
      );
  }

  /**
   * Create service item against acms aircraft.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createAcmsAircraftServiceItem$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAcmsAircraftServiceItem(
    params: {
      body: CreateAcmsAircraftServiceItemRequest;
    },
    context?: HttpContext
  ): Observable<AcmsAircraftServiceItemResponse> {
    return this.createAcmsAircraftServiceItem$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<AcmsAircraftServiceItemResponse>
        ): AcmsAircraftServiceItemResponse => r.body
      )
    );
  }

  /** Path part for operation `getAcmsAircraftServiceItemById()` */
  static readonly GetAcmsAircraftServiceItemByIdPath =
    '/api/v1/acms-aircraft-service-item/{id}';

  /**
   * fetch aircraft service items details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAcmsAircraftServiceItemById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAcmsAircraftServiceItemById$Response(
    params: {
      /**
       * The aircraft service item id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AcmsAircraftServiceItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftServiceItemService.GetAcmsAircraftServiceItemByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AcmsAircraftServiceItemResponse>;
        })
      );
  }

  /**
   * fetch aircraft service items details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAcmsAircraftServiceItemById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAcmsAircraftServiceItemById(
    params: {
      /**
       * The aircraft service item id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<AcmsAircraftServiceItemResponse> {
    return this.getAcmsAircraftServiceItemById$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<AcmsAircraftServiceItemResponse>
        ): AcmsAircraftServiceItemResponse => r.body
      )
    );
  }

  /** Path part for operation `deleteAcmsAircraftServiceItemById()` */
  static readonly DeleteAcmsAircraftServiceItemByIdPath =
    '/api/v1/acms-aircraft-service-item/{id}';

  /**
   * Delete ACMS aircraft service item  by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteAcmsAircraftServiceItemById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAcmsAircraftServiceItemById$Response(
    params: {
      /**
       * The aircraft service item id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftServiceItemService.DeleteAcmsAircraftServiceItemByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete ACMS aircraft service item  by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteAcmsAircraftServiceItemById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAcmsAircraftServiceItemById(
    params: {
      /**
       * The aircraft service item id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteAcmsAircraftServiceItemById$Response(
      params,
      context
    ).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateAcmsAircraftServiceItemById()` */
  static readonly UpdateAcmsAircraftServiceItemByIdPath =
    '/api/v1/acms-aircraft-service-item/{id}';

  /**
   * Update ACMS Aircraft service item.
   *
   * This API update ACMS aircraft service item.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAcmsAircraftServiceItemById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAcmsAircraftServiceItemById$Response(
    params: {
      /**
       * The aircraft id that needs to be updated
       */
      id: number;
      body: UpdateAcmsAircraftServiceItemRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AcmsAircraftServiceItemResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftServiceItemService.UpdateAcmsAircraftServiceItemByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AcmsAircraftServiceItemResponse>;
        })
      );
  }

  /**
   * Update ACMS Aircraft service item.
   *
   * This API update ACMS aircraft service item.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateAcmsAircraftServiceItemById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAcmsAircraftServiceItemById(
    params: {
      /**
       * The aircraft id that needs to be updated
       */
      id: number;
      body: UpdateAcmsAircraftServiceItemRequest;
    },
    context?: HttpContext
  ): Observable<AcmsAircraftServiceItemResponse> {
    return this.updateAcmsAircraftServiceItemById$Response(
      params,
      context
    ).pipe(
      map(
        (
          r: StrictHttpResponse<AcmsAircraftServiceItemResponse>
        ): AcmsAircraftServiceItemResponse => r.body
      )
    );
  }
}
